import { useCountry } from "../../contexts/CountryContext";
import { useLocation } from "react-router-dom";
import { Globe, Check } from "lucide-react";

const HIDE_ON_PATH_PREFIXES = ["/admin", "/login", "/register", "/reset-password"];

/**
 * One-time country picker — pops up on first visit when no `fg_country`
 * is saved locally and the user isn't signed in with a country on file.
 *
 * Tone: a calm welcome, not a popup ad.
 *   "Hi there 👋 — where would you like to shop from?"
 *
 * Defaults to UK if the user closes without choosing.
 */
export default function CountryPickerModal() {
  const ctx = useCountry();
  const location = useLocation();
  if (!ctx) return null;
  const onHiddenRoute = HIDE_ON_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));
  const { pickerOpen, closePicker, setCountry, supported, defaultCountry } = ctx;
  if (!pickerOpen || onHiddenRoute) return null;

  const handlePick = (code) => {
    setCountry(code);
  };
  const handleSkip = () => {
    // Treat skip as "use the default"
    setCountry(defaultCountry || "UK");
    closePicker();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-emerald-950/45 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="country-picker-title"
      data-testid="country-picker-modal"
    >
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300">
        <div className="p-6 sm:p-8">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 mb-4">
            <Globe className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Welcome to Feel Greats
          </p>
          <h2 id="country-picker-title" className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
            Where would you like to shop from?
          </h2>
          <p className="text-sm text-emerald-900/70 mt-3 leading-relaxed">
            We show different products, prices and guidance in each country. You can change this any time from the footer.
          </p>

          <div className="mt-5 grid gap-2.5" data-testid="country-picker-options">
            {(supported || []).map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => handlePick(c.code)}
                className="group flex items-center justify-between rounded-2xl border border-emerald-100 bg-white px-4 py-3.5 text-left hover:border-emerald-300 hover:shadow-sm transition"
                data-testid={`country-picker-option-${c.code}`}
              >
                <div>
                  <div className="text-sm font-semibold text-emerald-950">{c.label}</div>
                  <div className="text-[12px] text-emerald-900/65 mt-0.5">
                    Pricing in {c.currency}
                  </div>
                </div>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100 transition">
                  <Check className="w-4 h-4" strokeWidth={2} />
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleSkip}
            className="mt-4 text-[12px] text-emerald-700/70 hover:text-emerald-800 underline"
            data-testid="country-picker-skip"
          >
            Not sure — continue with United Kingdom
          </button>
        </div>
      </div>
    </div>
  );
}
